import { Component, effect, input, OnDestroy, OnInit, signal } from '@angular/core';
import { EditRideComponent } from '../../dialogs/edit-ride/edit-ride.component';
import { EditAutomaticRideComponent } from '../../dialogs/edit-automatic-ride/edit-automatic-ride.component';
import { MatDialog } from '@angular/material/dialog';
import { AutomaticallyPlannedRide, User, Ride } from 'src/app/interfaces';
import {
  onSnapshot,
  collection,
  orderBy,
  query,
  where,
  Unsubscribe,
  doc,
  deleteDoc,
  getDocs,
  setDoc,
} from 'firebase/firestore';
import { dataStore, db } from 'src/app/app.component';
import moment from 'moment';
import { AutoPlanDialogComponent } from '../../dialogs/auto-plan/auto-plan-dialog/auto-plan-dialog.component';
import {
  PlanUnassignedRideComponent,
  PlanUnassignedRideType,
} from '../../dialogs/plan-unassigned-ride/plan-unassigned-ride.component';
import { LogType, Role, PlanType } from 'src/app/enums';
import { AutoPlanService } from './auto-plan-service';
import { ViewMode } from '../../planner.component';
import { WarningDialog } from 'src/app/global-dialogs/warning/warning.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { MatTabGroup, MatTab, MatTabLabel } from '@angular/material/tabs';
import { AutoPlanItemComponent } from '../auto-plan-item/auto-plan-item.component';
import { MatBadge } from '@angular/material/badge';
import { getFullLocationString } from '../../helpers/ride-helper';
import { valueToDate } from 'src/app/globals';

@Component({
  selector: 'app-auto-plan',
  templateUrl: './auto-plan.component.html',
  styleUrls: ['./auto-plan.component.scss'],
  imports: [
    MatIcon,
    MatButton,
    MatProgressSpinner,
    MatIconButton,
    MatTooltip,
    MatTabGroup,
    MatTab,
    AutoPlanItemComponent,
    MatTabLabel,
    MatBadge,
  ],
})
export class AutoPlanComponent implements OnInit, OnDestroy {
  date = input<Date>(new Date());
  minimized = input(false);
  planTypeChanged = input<(value: PlanType, view: ViewMode) => void>(() => {});
  dateChanged = input<(value: Date) => void>(() => {});
  searchValue = input<string>('');
  // Track previous values to detect changes
  previousInputValues = {
    date: null as Date | null,
    minimized: null as boolean | null,
    searchValue: null as string | null,
  };

  isSuperUser: boolean = dataStore.rootUser.rights === 'admin' ? true : false;
  plannedRides = signal<AutomaticallyPlannedRide[]>([]);
  unassignedRides = signal<AutomaticallyPlannedRide[]>([]);
  filteredPlannedRides = signal<AutomaticallyPlannedRide[]>([]);
  filteredUnassignedRides = signal<AutomaticallyPlannedRide[]>([]);
  isAdmin: boolean = dataStore.orgUser.roles?.includes(Role.Administrator) ? true : false;
  isPlanner: boolean = dataStore.orgUser.roles?.includes(Role.Planner) ? true : false;
  isRidePlanner: boolean = dataStore.orgUser.roles?.includes(Role.RidePlanner) ? true : false;
  plannedRidesUnsub: Unsubscribe | null = null;
  unassignedRidesUnsub: Unsubscribe | null = null;
  moment = moment;
  selectedIndex: number = 0;
  isResetting = signal(false);

  constructor(
    public dialog: MatDialog,
    private autoPlanService: AutoPlanService,
    private snackBar: MatSnackBar,
  ) {
    effect(() => {
      const date = this.date();
      const minimized = this.minimized();
      const searchValue = this.searchValue();

      const changes: Record<string, boolean> = {};
      if (this.previousInputValues.date?.getTime() !== date?.getTime()) {
        changes['date'] = true;
        this.previousInputValues.date = date;
      }
      if (this.previousInputValues.minimized !== minimized) {
        changes['minimized'] = true;
        this.previousInputValues.minimized = minimized;
      }
      if (this.previousInputValues.searchValue !== searchValue) {
        changes['searchValue'] = true;
        this.previousInputValues.searchValue = searchValue;
      }

      const meaningfulChanges = Object.keys(changes).filter(
        (key) =>
          key !== 'planTypeChanged' &&
          key !== 'dateChanged' &&
          key !== 'searchValue' &&
          changes[key],
      );
      if (meaningfulChanges.length === 0) {
        if (changes['searchValue']) {
          this.filterRides('plannedRides');
          this.filterRides('unassignedRides');
        }
        return;
      }

      if (this.autoPlanService.getLastChangeType() === 'standard') {
        this.autoPlanService.setLastDate(date);
      }
      if (this.plannedRidesUnsub) {
        this.plannedRidesUnsub();
        this.plannedRidesUnsub = null;
      }
      if (this.unassignedRidesUnsub) {
        this.unassignedRidesUnsub();
        this.unassignedRidesUnsub = null;
      }
      this.getPlannedRides();
      this.getUnassignedRides();
    });
  }

  ngOnInit() {
    if (this.autoPlanService.getLastChangeType() === 'background') {
      const lastDate = this.autoPlanService.getLastDate();
      this.dateChanged()(lastDate);
    } else {
      this.autoPlanService.setLastDate(this.date());
    }
    this.selectedIndex = this.autoPlanService.getSelectedIndex();
  }

  ngOnDestroy() {
    // Clean up subscription when component is destroyed
    if (this.plannedRidesUnsub) {
      this.plannedRidesUnsub();
    }
    if (this.unassignedRidesUnsub) {
      this.unassignedRidesUnsub();
    }
  }

  editRide(ride: AutomaticallyPlannedRide) {
    console.log('Editing ride: ', ride);
    const users = {};
    users[ride.passenger.id] = { ...ride.passenger } as User;
    const rideCopy = {
      ...ride,
      start: ride.start.toDate(),
      end: ride.end.toDate(),
      users: users,
      rideProduct: ride.passenger.rideProduct,
    };
    console.log('Ride copy: ', rideCopy);
    if (!rideCopy.planningCompleted && !rideCopy.rejected) {
      const dialogRef = this.dialog.open(EditAutomaticRideComponent, {
        panelClass: ['fullscreen-dialog'],
        data: {
          ride: rideCopy,
          rideId: ride.id,
        },
      });
    } else if (rideCopy.planningCompleted && rideCopy.rejected) {
      const dialogRef = this.dialog.open(PlanUnassignedRideComponent, {
        width: '500px',
        data: {
          passenger: rideCopy.passenger,
          start: rideCopy.start,
          end: rideCopy.end,
          rideProduct: rideCopy.rideProduct,
          type: PlanUnassignedRideType.Unassigned,
          unassignedRideId: ride.id,
        },
        autoFocus: false,
      });
      dialogRef.componentInstance.planTypeChangeRequest.subscribe((value: PlanType) => {
        // const viewMode = value === PlanType.Rides ? ViewMode.Week : ViewMode.Day;
        const viewMode = ViewMode.Day;
        this.planTypeChanged()(value, viewMode);
        this.autoPlanService.setLastChangeType('background');
        this.autoPlanService.setLastDate(this.date());
      });
    }
  }

  getPlannedRides() {
    const startOfDay = new Date(this.date());
    const endOfDay = new Date(this.date());
    startOfDay.setHours(0, 0, 0, 0);
    endOfDay.setHours(23, 59, 59, 999);

    this.plannedRidesUnsub = onSnapshot(
      query(
        collection(db, `organisations/${dataStore.rootUser.selectedOrgId}/automaticRides`),
        orderBy('start'),
        where('planningCompleted', '==', false),
        where('rejected', '==', false),
        where('start', '>=', startOfDay),
        where('start', '<=', endOfDay),
      ),
      (snapshot) => {
        const plannedRides: AutomaticallyPlannedRide[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data() as AutomaticallyPlannedRide;
          const id = doc.id;
          plannedRides.push({ id, ...data });
        });
        this.plannedRides.set(plannedRides);
        this.filterRides('plannedRides');
      },
    );
  }

  getUnassignedRides() {
    const startOfDay = new Date(this.date());
    const endOfDay = new Date(this.date());
    startOfDay.setHours(0, 0, 0, 0);
    endOfDay.setHours(23, 59, 59, 999);

    this.unassignedRidesUnsub = onSnapshot(
      query(
        collection(db, `organisations/${dataStore.rootUser.selectedOrgId}/automaticRides`),
        orderBy('start'),
        where('planningCompleted', '==', true),
        where('rejected', '==', true),
        where('start', '>=', startOfDay),
        where('start', '<=', endOfDay),
      ),
      (snapshot) => {
        const plannedRides: AutomaticallyPlannedRide[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data() as AutomaticallyPlannedRide;
          const id = doc.id;
          plannedRides.push({ id, ...data });
        });
        this.unassignedRides.set(plannedRides);
        this.filterRides('unassignedRides');
      },
    );
  }

  filterRides(type: 'plannedRides' | 'unassignedRides') {
    let filteredRides = [];

    filteredRides = this[type]()
      .filter((ride) => {
        if (ride.passenger) {
          if (ride.passenger?.title.toLowerCase().includes(this.searchValue().toLowerCase())) {
            return ride;
          }

          if (
            getFullLocationString(ride.passenger.from)
              .toLowerCase()
              .includes(this.searchValue().toLowerCase()) ||
            getFullLocationString(ride.passenger.to)
              .toLowerCase()
              .includes(this.searchValue().toLowerCase())
          ) {
            return ride;
          }
        }
      })
      .sort((a, b) => (valueToDate(a.start) > valueToDate(b.start) ? 1 : -1));
    if (type === 'plannedRides') {
      this.filteredPlannedRides.set([...filteredRides]);
    } else if (type === 'unassignedRides') {
      this.filteredUnassignedRides.set([...filteredRides]);
    }
  }

  async deleteUnassignedRide(rideId: string) {
    this.dialog
      .open(WarningDialog, {
        width: '600px',
        data: { dialogType: 'deleteUnassignedRide', optionalData: { deleteMode: true } },
      })
      .afterClosed()
      .subscribe(async (result: any) => {
        if (result) {
          await deleteDoc(
            doc(db, `organisations/${dataStore.selectedOrganisation.id}/automaticRides/${rideId}`),
          );
          this.snackBar.open('De restrit is verwijderd.', 'X', {
            duration: 5000,
          });
        }
      });
  }

  planAutomaticRides() {
    const dialogRef = this.dialog.open(AutoPlanDialogComponent, {
      autoFocus: false,
      data: { date: this.date() },
    });
  }

  resetAutomaticRides() {
    this.dialog
      .open(WarningDialog, {
        width: '600px',
        data: {
          dialogType: 'resetAutomaticRides',
          optionalData: { date: moment(this.date()).format('DD MMMM YYYY') },
        },
      })
      .afterClosed()
      .subscribe(async (result: any) => {
        if (!result) return;

        const logId = await this.autoPlanService.createAutomaticPlanningActionLog(
          LogType.AutomaticPlanningReset,
          this.date(),
        );

        this.isResetting.set(true);
        try {
          const startOfDay = new Date(this.date());
          const endOfDay = new Date(this.date());
          startOfDay.setHours(0, 0, 0, 0);
          endOfDay.setHours(23, 59, 59, 999);

          const q = query(
            collection(db, `organisations/${dataStore.rootUser.selectedOrgId}/automaticRides`),
            orderBy('start'),
            where('start', '>=', startOfDay),
            where('start', '<=', endOfDay),
          );
          const automaticRides = (await getDocs(q)).docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as AutomaticallyPlannedRide[];
          for (const ride of automaticRides) {
            await setDoc(
              doc(
                db,
                `organisations/${dataStore.rootUser.selectedOrgId}/automaticRides/${ride.id}`,
              ),
              {
                planningCompleted: false,
                rejected: false,
              },
              { merge: true },
            );
          }

          const ridesQuery = query(
            collection(db, `organisations/${dataStore.rootUser.selectedOrgId}/rides`),
            orderBy('start'),
            where('start', '>=', startOfDay),
            where('start', '<=', endOfDay),
            where('automaticallyPlanned', '==', true),
          );
          const rides = (await getDocs(ridesQuery)).docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as Ride[];
          for (const ride of rides) {
            await deleteDoc(
              doc(db, `organisations/${dataStore.rootUser.selectedOrgId}/rides/${ride.id}`),
            );
          }

          await this.autoPlanService.updateAutomaticPlanningActionStatus(logId, 'completed');

          this.snackBar.open('Restritten zijn succesvol gereset.', 'X', {
            duration: 5000,
          });
        } catch (error) {
          console.error('Error resetting automatic rides:', error);
          await this.autoPlanService.updateAutomaticPlanningActionStatus(logId, 'failed');
          this.snackBar.open('Er is een fout opgetreden bij het resetten van restritten.', 'X', {
            duration: 5000,
          });
        } finally {
          this.isResetting.set(false);
        }
      });
  }
}
